/**
 * Widget editor rules (no Angular): widget types and what each one uses, a new widget, type / source changes,
 * validation, aggregate operators. Covered by widget-editor.model.spec.ts.
 */
import type { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { parseDisplayOptions } from '../../../../iupics-widgets/widget-display';

/** codes of WidgetViewType / CountListViewType (widget-center-json), kept here so the rules load without Angular */
export const WidgetViewType = {
  COUNT_LIST: 'CLW',
  GEN_WIDGET: 'GEN',
  DOUGHNUT_CHART: 'DNC',
  POLAR_CHART: 'PLC',
  RADAR_CHART: 'RDC',
  SCATTER_CHART: 'SCC',
  BUBBLE_CHART: 'BUC',
  PIE_CHART: 'PIC',
  LINE_CHART: 'LIC',
  COLUMN_CHART: 'CLC',
  BAR_CHART: 'BRC',
  GRID_VIEW_WIDGET: 'GVW',
  METABASE_CHART: 'MBC',
} as const;
export type WidgetTypeCode = (typeof WidgetViewType)[keyof typeof WidgetViewType];
export const CountListViewType = { COUNT_VIEW: 'CVT', LIST_VIEW: 'LVT' } as const;

/** where the data comes from: a window tab, a table, a web page, nothing (generic component) */
export type WidgetSource = 'tab' | 'table' | 'url' | 'none';
export type WidgetFamily = 'count' | 'chart' | 'grid' | 'web' | 'generic';
export type AggregateOperator = 'SUM' | 'AVG' | 'CNT' | 'MIN' | 'MAX';
export const AGGREGATE_OPERATORS: AggregateOperator[] = ['SUM', 'AVG', 'CNT', 'MIN', 'MAX'];
export const SORTS = ['ASC', 'DSC'] as const;
/** seconds; 0 = no automatic refresh */
export const MIN_REFRESH_SECONDS = 5;

export interface WidgetTypeMeta {
  code: WidgetTypeCode;
  family: WidgetFamily;
  icon: string;
  angularClass: string;
  sources: WidgetSource[];
  /** chart axes: category (x / slices) and series */
  category: boolean;
  serie: boolean;
  /** several datasets needed to draw (polar, radar, scatter, bubble): the series column is required */
  needsSerie: boolean;
  aggregate: boolean;
  /** count: thresholds and colours, count / list view */
  thresholds: boolean;
  view: boolean;
  /** unit, precision, rounding, coefficient */
  format: boolean;
}

const COUNT_CLASS = 'CountListWidgetUiComponent';
const CHART_CLASS = 'ChartWidgetComponent';

function chart(code: WidgetTypeCode, icon: string, needsSerie = false): WidgetTypeMeta {
  return {
    code,
    family: 'chart',
    icon,
    angularClass: CHART_CLASS,
    sources: ['tab', 'table'],
    category: true,
    serie: true,
    needsSerie,
    aggregate: true,
    thresholds: false,
    view: false,
    format: true,
  };
}

/** order of the type picker */
export const WIDGET_TYPES: WidgetTypeMeta[] = [
  {
    code: WidgetViewType.COUNT_LIST,
    family: 'count',
    icon: 'fa-solid fa-hashtag',
    angularClass: COUNT_CLASS,
    sources: ['tab', 'table'],
    category: false,
    serie: false,
    needsSerie: false,
    aggregate: true,
    thresholds: true,
    view: true,
    format: true,
  },
  chart(WidgetViewType.COLUMN_CHART, 'fa-solid fa-chart-column'),
  chart(WidgetViewType.BAR_CHART, 'fa-solid fa-chart-bar'),
  chart(WidgetViewType.LINE_CHART, 'fa-solid fa-chart-line'),
  chart(WidgetViewType.PIE_CHART, 'fa-solid fa-chart-pie'),
  chart(WidgetViewType.DOUGHNUT_CHART, 'fa-solid fa-circle-dot'),
  chart(WidgetViewType.POLAR_CHART, 'fa-solid fa-bullseye', true),
  chart(WidgetViewType.RADAR_CHART, 'fa-solid fa-draw-polygon', true),
  chart(WidgetViewType.SCATTER_CHART, 'fa-solid fa-braille', true),
  chart(WidgetViewType.BUBBLE_CHART, 'fa-solid fa-circle-nodes', true),
  {
    code: WidgetViewType.GRID_VIEW_WIDGET,
    family: 'grid',
    icon: 'fa-solid fa-table',
    angularClass: 'GridWidgetComponent',
    sources: ['tab'],
    category: false,
    serie: false,
    needsSerie: false,
    aggregate: false,
    thresholds: false,
    view: false,
    format: false,
  },
  {
    code: WidgetViewType.METABASE_CHART,
    family: 'web',
    icon: 'fa-solid fa-globe',
    angularClass: 'MetabaseWidgetComponent',
    sources: ['url'],
    category: false,
    serie: false,
    needsSerie: false,
    aggregate: false,
    thresholds: false,
    view: false,
    format: false,
  },
  {
    code: WidgetViewType.GEN_WIDGET,
    family: 'generic',
    icon: 'fa-solid fa-puzzle-piece',
    angularClass: '',
    sources: ['none'],
    category: false,
    serie: false,
    needsSerie: false,
    aggregate: false,
    thresholds: false,
    view: false,
    format: false,
  },
];

export function typeMeta(code: string | null | undefined): WidgetTypeMeta {
  return WIDGET_TYPES.find((t) => t.code === code) ?? WIDGET_TYPES[0];
}

/** colours of a new widget (theme values when the theme has them) */
export interface WidgetColors {
  normal: string;
  warning: string;
  alert: string;
}
export const DEFAULT_COLORS: WidgetColors = { normal: '#63a856', warning: '#b4892d', alert: '#9b0015' };

/** grid preference ("saved view") a widget is created from */
export interface WidgetFromView {
  name: string;
  gridRequest: string;
  gridState: string;
  userGridPreferenceId: number;
  tabId: number;
  tableId: number;
  windowId: number;
}

export function newWidget(owner: { roleId: number; userId: number }, colors: WidgetColors = DEFAULT_COLORS, view?: WidgetFromView): IupicsWidget {
  return {
    id: 0,
    name: view?.name ?? null,
    description: null,
    angularClass: COUNT_CLASS,
    widgetType: WidgetViewType.COUNT_LIST,
    defaultView: CountListViewType.COUNT_VIEW,
    gridRequest: view?.gridRequest ?? null,
    gridState: view?.gridState ?? null,
    userGridPreferenceId: view?.userGridPreferenceId ?? null,
    tabId: view?.tabId ?? null,
    roleId: owner.roleId,
    tableId: view?.tableId ?? null,
    windowId: view?.windowId ?? null,
    recordLimit: 0,
    normalLevel: 0,
    normalColor: colors.normal,
    alertLevel: 0,
    alertColor: colors.alert,
    warningColor: colors.warning,
    userId: owner.userId,
    multiplier: null,
    categoryColumnId: null,
    categorySort: null,
    serieColumnId: null,
    serieSort: null,
    aggregateColumnId: null,
    aggregateSort: null,
    aggregateOperator: null,
    isRounded: true,
    measureUnit: '',
    precision: 2,
    widgetRoles: [],
    iframe: null,
    refreshRate: 0,
    displayOptions: null,
  };
}

/** the widget comes from a saved view of a grid: its source and filters are those of the view */
export function fromSavedView(w: IupicsWidget): boolean {
  return (w?.userGridPreferenceId ?? 0) > 0;
}

/** source of a widget as the editor shows it */
export function sourceOf(w: IupicsWidget): WidgetSource {
  const meta = typeMeta(w.widgetType);
  if (meta.sources.length === 1) return meta.sources[0];
  if (w.tableId > 0 && !(w.tabId > 0)) return 'table';
  return 'tab';
}

/** chart / count columns cleared (the source changed: they belonged to the previous table) */
export function clearColumns(w: IupicsWidget): IupicsWidget {
  return {
    ...w,
    categoryColumnId: null,
    categorySort: null,
    serieColumnId: null,
    serieSort: null,
    aggregateColumnId: null,
    aggregateOperator: null,
    aggregateSort: null,
  };
}

/** another source: what belonged to the previous one is cleared (columns, filters) */
export function setSource(w: IupicsWidget, source: WidgetSource): IupicsWidget {
  if (fromSavedView(w)) return w;
  const base = clearColumns({ ...w, gridRequest: null, gridState: null });
  switch (source) {
    case 'tab':
      return { ...base, tableId: null, iframe: null };
    case 'table':
      return { ...base, windowId: null, tabId: null, iframe: null };
    case 'url':
      return { ...base, windowId: null, tabId: null, tableId: null };
    default:
      return { ...base, windowId: null, tabId: null, tableId: null, iframe: null };
  }
}

/** another type: its component and view, and only the settings it uses are kept */
export function applyType(w: IupicsWidget, code: WidgetTypeCode | string, aggregateColumn: { textLike: boolean } | null = null): IupicsWidget {
  const meta = typeMeta(code);
  let next: IupicsWidget = {
    ...w,
    widgetType: meta.code,
    angularClass: meta.family === 'generic' ? (typeMeta(w.widgetType).family === 'generic' ? w.angularClass : '') : meta.angularClass,
    defaultView: meta.view ? (w.defaultView === CountListViewType.LIST_VIEW ? CountListViewType.LIST_VIEW : CountListViewType.COUNT_VIEW) : null,
  };
  if (!meta.category) next = { ...next, categoryColumnId: null, categorySort: null };
  if (!meta.serie) next = { ...next, serieColumnId: null, serieSort: null };
  if (!meta.aggregate) next = { ...next, aggregateColumnId: null, aggregateOperator: null, aggregateSort: null };
  const current = sourceOf(w);
  if (!meta.sources.includes(current) && !fromSavedView(next)) next = setSource(next, meta.sources[0]);
  if (meta.family === 'chart' || meta.family === 'count') {
    // operator no longer allowed (e.g. MIN on a text column of a count)
    if (next.aggregateOperator && !operatorsFor(aggregateColumn, meta.code).includes(next.aggregateOperator as AggregateOperator)) {
      next = { ...next, aggregateOperator: null };
    }
  }
  return next;
}

/** operators the server accepts for the column: text-like columns only count (and min / max on charts) */
export function operatorsFor(column: { textLike: boolean } | null, code: string): AggregateOperator[] {
  if (!column || !column.textLike) return [...AGGREGATE_OPERATORS];
  return code === WidgetViewType.COUNT_LIST ? ['CNT'] : ['CNT', 'MIN', 'MAX'];
}

/** field -> i18n key of the problem */
export type WidgetErrors = Partial<Record<keyof IupicsWidget | 'source', string>>;

const URL_RE = /^https?:\/\/\S+$/i;

export function isWebAddress(value: string | null | undefined): boolean {
  return !!value && URL_RE.test(value.trim());
}

/** what blocks the save, by field (empty: the widget can be saved) */
export function validate(w: IupicsWidget, aggregateColumn: { textLike: boolean } | null = null): WidgetErrors {
  const e: WidgetErrors = {};
  const meta = typeMeta(w.widgetType);
  const required = 'widgetEditor.errors.required';
  if (!w.name?.trim()) e.name = required;
  if (!fromSavedView(w)) {
    switch (sourceOf(w)) {
      case 'tab':
        if (!(w.windowId > 0)) e.windowId = required;
        if (!(w.tabId > 0)) e.tabId = required;
        break;
      case 'table':
        if (!(w.tableId > 0)) e.tableId = required;
        break;
      case 'url':
        if (!w.iframe?.trim()) e.iframe = required;
        else if (!isWebAddress(w.iframe)) e.iframe = 'widgetEditor.errors.url';
        break;
    }
  }
  if (meta.family === 'generic' && !w.angularClass) e.angularClass = required;
  if (meta.family === 'chart') {
    if (!w.categoryColumnId) e.categoryColumnId = required;
    if (meta.needsSerie && !w.serieColumnId) e.serieColumnId = 'widgetEditor.errors.serieNeeded';
  }
  // a count needs its value (the list view alone does not)
  const needsAggregate = meta.family === 'chart' || (meta.family === 'count' && w.defaultView !== CountListViewType.LIST_VIEW);
  if (needsAggregate) {
    if (!w.aggregateColumnId) e.aggregateColumnId = required;
    if (!w.aggregateOperator) e.aggregateOperator = required;
    else if (!operatorsFor(aggregateColumn, w.widgetType).includes(w.aggregateOperator as AggregateOperator))
      e.aggregateOperator = 'widgetEditor.errors.operator';
  }
  if (w.recordLimit != null && w.recordLimit < 0) e.recordLimit = 'widgetEditor.errors.positive';
  if (w.precision != null && (w.precision < 0 || w.precision > 10)) e.precision = 'widgetEditor.errors.precision';
  if (w.refreshRate > 0 && w.refreshRate < MIN_REFRESH_SECONDS) e.refreshRate = 'widgetEditor.errors.refresh';
  // an explicit direction must match the order of the levels (else the colours would contradict it)
  if (meta.thresholds) {
    const direction = parseDisplayOptions(w.displayOptions).alertDirection;
    const normal = w.normalLevel ?? 0;
    const alert = w.alertLevel ?? 0;
    if (direction && (normal || alert) && normal !== alert && (direction === 'higherWorse') !== alert > normal)
      e.alertLevel = direction === 'higherWorse' ? 'widgetEditor.errors.alertAbove' : 'widgetEditor.errors.alertBelow';
  }
  return e;
}

/** the fields of the widget the editor saves (roles compared apart) */
const SAVED_FIELDS: (keyof IupicsWidget)[] = [
  'name', 'description', 'angularClass', 'widgetType', 'defaultView', 'gridRequest', 'gridState', 'tabId', 'tableId',
  'windowId', 'recordLimit', 'normalLevel', 'normalColor', 'alertLevel', 'alertColor', 'warningColor', 'multiplier',
  'categoryColumnId', 'categorySort', 'serieColumnId', 'serieSort', 'aggregateColumnId', 'aggregateSort',
  'aggregateOperator', 'isRounded', 'measureUnit', 'precision', 'iframe', 'refreshRate', 'displayOptions',
];

function norm(v: unknown): unknown {
  return v === undefined || v === '' ? null : v;
}

export function isDirty(a: IupicsWidget | null, b: IupicsWidget | null): boolean {
  if (!a || !b) return a !== b;
  return SAVED_FIELDS.some((k) => norm(a[k]) !== norm(b[k]));
}

/** fields the preview data depends on: when only others change (colours, unit...), the data is not read again */
const DATA_FIELDS: (keyof IupicsWidget)[] = [
  'widgetType', 'angularClass', 'gridRequest', 'tabId', 'tableId', 'windowId', 'recordLimit', 'categoryColumnId',
  'categorySort', 'serieColumnId', 'serieSort', 'aggregateColumnId', 'aggregateSort', 'aggregateOperator', 'iframe',
  'defaultView',
];

export function dataKey(w: IupicsWidget): string {
  return JSON.stringify(DATA_FIELDS.map((k) => norm(w[k])));
}

/** copy for "Duplicate": new id, "(copy)" name, personal to the user */
export function duplicate(w: IupicsWidget, owner: { roleId: number; userId: number }, suffix: string): IupicsWidget {
  return {
    ...w,
    id: 0,
    name: `${w.name ?? ''} ${suffix}`.trim(),
    roleId: owner.roleId,
    userId: owner.userId,
    widgetRoles: [],
    newWidgetRoles: undefined,
    userGridPreferenceId: null,
  };
}

/** what a column holds, for its marker in the lists (display type of the dictionary) */
export type ColumnKind = 'number' | 'date' | 'yesno' | 'text' | 'reference' | 'list';

export function columnKind(displayType: number): ColumnKind {
  switch (displayType) {
    case 11: // Integer
    case 12: // Amount
    case 22: // Number
    case 29: // Quantity
    case 37: // Cost + Price
      return 'number';
    case 15: // Date
    case 16: // Date + Time
    case 24: // Time
      return 'date';
    case 20: // Yes-No
      return 'yesno';
    case 17: // List
      return 'list';
    case 13: // ID
    case 18: // Table
    case 19: // Table direct
    case 21: // Location
    case 25: // Account
    case 30: // Search
    case 31: // Locator
    case 33: // Assignment
    case 35: // Product attribute
      return 'reference';
    default:
      return 'text';
  }
}

export const COLUMN_KIND_ICONS: Record<ColumnKind, string> = {
  number: 'fa-solid fa-hashtag',
  date: 'fa-regular fa-calendar',
  yesno: 'fa-solid fa-toggle-on',
  text: 'fa-solid fa-font',
  reference: 'fa-solid fa-link',
  list: 'fa-solid fa-list-ul',
};

/** columns for the value field: numbers first (the ones a sum or an average makes sense on), each group by name */
export function valueColumnsFirst<T extends { name: string; displayType: number }>(columns: T[]): T[] {
  const rank = (c: T) => (columnKind(c.displayType) === 'number' ? 0 : 1);
  return [...columns].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** one condition of the filters of a widget, for a sentence ("Status is Open") */
export interface FilterPhrase {
  column: string;
  label: string;
  /** operator of the grid filter (equals, contains, between, isNull...) */
  operator: string;
  values: string[];
}

function shownValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(shownValue).filter((x) => x).join(' - ');
  if (typeof v === 'object') {
    const o = v as { displayValue?: unknown; id?: unknown };
    return String(o.displayValue ?? o.id ?? '');
  }
  const text = String(v);
  // dates sent as ISO: the day is enough
  return /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.slice(0, 10) : text;
}

/** the filters stored with a widget (gridRequest), as phrases; label: name of a column by its column name */
export function describeFilters(gridRequest: string | null | undefined, label: (column: string) => string | null): FilterPhrase[] {
  if (!gridRequest) return [];
  let request: { filterModel?: Record<string, { operators?: string[]; values?: unknown[] }> };
  try {
    request = JSON.parse(gridRequest);
  } catch {
    return [];
  }
  const out: FilterPhrase[] = [];
  for (const [column, entry] of Object.entries(request?.filterModel ?? {})) {
    const operators = entry?.operators ?? [];
    const values = entry?.values ?? [];
    const count = Math.max(operators.length, 1);
    for (let i = 0; i < count; i++) {
      const operator = operators[i] ?? 'equals';
      const value = values[i];
      const shown = operator === 'isNull' || operator === 'isNotNull' ? [] : Array.isArray(value) && operator !== 'between' ? value.map(shownValue) : [shownValue(value)];
      out.push({ column, label: label(column) || column, operator, values: shown.filter((x) => x !== '') });
    }
  }
  return out;
}
